import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react"
import { AxiosError } from "axios"
import classNames from "classnames"
import cn from "classnames"
import { useLocation } from "react-router-dom"
import { useFeature } from "@growthbook/growthbook-react"

import {
    getEmailErrorMessage,
    getNameErrorMessage,
    isEmpty,
    isNameValid,
    VERIFY_CODE_LENGTH,
    VERIFY_CODE_PLACEHOLDER,
} from "../common"
import { CompanyRegisterStorage } from "../Company/common/companyAuthStorage"
import { useClientContext } from "../common/ClientContext"
import { ActionButtons, ErrorPage, SuccessLoginPage, VerificationCode } from "../components"

import { Header, LoginCard, RegistrationCard } from "./components"
import {
    CLIENT_AUTHORIZATION_PLACE,
    CLIENT_STATUS,
    DEFAULT_ERROR_MESSAGE,
    INPUT_CLASS,
} from "./common/constant"
import {
    LoginLinkResponse,
    LoginLinkResponseError,
    RegisterLinkResponseError,
} from "./common/interfaces"
import { fetchAuthByCode, FetchByCode, fetchRegisterByCode, fetchVerifyByCode } from "./common/api"
import { SecondHeader } from "./components/Header/SecondHeader"
import { getFormModel } from "./models"
import { getNextLink, getPromoCode } from "./parseQuery"
import { getErrorMessage } from "./common/getErrorMessage"
import styles from "./style.module.scss"
import { getVerificationError } from "./common/getVerificationError"

import { Spinner } from "components/Spinner"
import { format } from "utils/datetime-format-utils"
import { useCountdown } from "utils/hooks"
import { validateEmail as isEmailValid } from "utils/helpers"
import { UserAccount } from "utils/UserAccount"
import { IS_PRODUCTION } from "utils/env-management"
import { Text } from "@components-branded/Text"

export const ClientLoginForm = ({
    byPromocode,
    place,
    firstRegistration,
}: {
    byPromocode?: boolean
    place?: CLIENT_AUTHORIZATION_PLACE
    firstRegistration?: boolean
}) => {
    const { value: experimentClosedProduct } = useFeature<boolean>("closed-product")

    const location = useLocation()
    const [email, setEmail] = useState("")
    const [name, setName] = useState("")
    const [status, setStatus] = useState(CLIENT_STATUS.CODE)
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState<ReactNode>(
        experimentClosedProduct ? undefined : DEFAULT_ERROR_MESSAGE
    )
    const [isAuthorization, setIsAuthorization] = useState(
        firstRegistration ? false : location.hash !== "#registration"
    )
    const [showErrors, setShowErrors] = useState(false)
    const [nameError, setNameError] = useState(false)
    const [emailError, setEmailError] = useState(false)
    const [promocodeError, setPromocodeError] = useState(false)
    const [emailValidationError, setEmailValidationError] = useState(false)
    const [nameValidationError, setNameValidationError] = useState(false)
    const [successData, setSuccessData] = useState({ email: "" })
    const [isSecondPage, setSecondPage] = useState(false)

    const [model, setModel] = useState(getFormModel({ byPromocode, isAuthorization }))
    const [promocode, setPromocode] = useState(getPromoCode() ?? "")

    const [code, setCode] = useState<string>(
        Array(VERIFY_CODE_LENGTH).fill(VERIFY_CODE_PLACEHOLDER).join("")
    )
    const [resendTimeout, setResendTimeout] = useState(CompanyRegisterStorage.getResendTimeout())
    const [fetchByCodeResponse, setFetchByCodeResponse] = useState<FetchByCode>()
    const countdown = useCountdown(resendTimeout)
    const [, { reloadClient }] = useClientContext()
    const [verificationCode, setVerificationCode] = useState<string>()

    useEffect(() => {
        model.track()
    }, [model.kind])

    const validateName = () => {
        isEmpty(name) && setNameError(true)
        setNameValidationError(isNameValid(name))
    }

    const validateEmail = () => {
        isEmpty(email) && setEmailError(true)
        setEmailValidationError(!isEmailValid(email))
    }

    const validatePromocode = () => {
        isEmpty(promocode) && setPromocodeError(true)
    }

    useEffect(() => {
        validateName()
        if (isAuthorization) {
            setNameError(false)
            setNameValidationError(false)
        }
        validateEmail()
        if (byPromocode) {
            validatePromocode()
        }
    }, [email, name, promocode, isAuthorization])

    useEffect(() => {
        setShowErrors(false)
        switchView(isAuthorization)
    }, [isAuthorization])

    useEffect(() => {
        const digitLength = [...code].filter((char) => /\d/.test(char)).length

        if (digitLength === VERIFY_CODE_LENGTH) {
            void handleClickCode()
        }
    }, [code])

    const emailInputStyles = classNames(INPUT_CLASS, {
        "contact-request-popup__input-error": (emailError || emailValidationError) && showErrors,
    })

    const nameInputStyles = classNames(INPUT_CLASS, {
        "contact-request-popup__input-error": (nameError || nameValidationError) && showErrors,
    })

    const promocodeInputStyles = classNames(INPUT_CLASS, {
        "contact-request-popup__input-error": promocodeError && showErrors,
    })

    const promocodeErrorMessage = useMemo(() => {
        if (showErrors && promocodeError) {
            return "Укажите промокод."
        }
    }, [promocodeError, showErrors])

    const onSuccessSubmit = (data: LoginLinkResponse) => {
        setSecondPage(true)
        UserAccount.storeSignIn({ email: data?.email })

        if (!IS_PRODUCTION) {
            const link = document.createElement("a")
            link.innerText = data?.link
            link.setAttribute("style", "font-size: 20px;color: red;")
            link.setAttribute("href", data?.link)
            document.body.prepend(link)
        }

        setSuccessData(data)
        setStatus(CLIENT_STATUS.SUCCESS)
        setIsLoading(false)
    }

    const onErrorSubmit = (
        error: AxiosError<LoginLinkResponseError | RegisterLinkResponseError>
    ) => {
        setSecondPage(true)
        setErrorMessage(getErrorMessage(error))
        setStatus(CLIENT_STATUS.ERROR)
        setIsLoading(false)
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (
            nameError ||
            emailError ||
            emailValidationError ||
            nameValidationError ||
            (byPromocode && !isAuthorization && promocodeError)
        ) {
            setShowErrors(true)
            return
        }

        setIsLoading(true)

        if (experimentClosedProduct && place === CLIENT_AUTHORIZATION_PLACE.MODAL) {
            await authByCode()
            return
        }

        model
            .request(
                { next: getNextLink() },
                {
                    email,
                    name: !isAuthorization ? name : undefined,
                    promo_code: byPromocode && !isAuthorization ? promocode : undefined,
                }
            )
            .then(onSuccessSubmit, onErrorSubmit)
    }

    const switchView = (setAuthorization: boolean) => {
        setSecondPage(false)
        setIsAuthorization(setAuthorization)
        setModel(getFormModel({ byPromocode, isAuthorization: setAuthorization }))
        setStatus(CLIENT_STATUS.FORM)
    }

    const authByCode = async () => {
        try {
            const data = isAuthorization
                ? await fetchAuthByCode(email)
                : await fetchRegisterByCode(email, name)
            setStatus(CLIENT_STATUS.CODE)
            setFetchByCodeResponse(data)
            const resendTimeout = new Date().getTime() + data.resend_timeout * 1000
            setResendTimeout(new Date(resendTimeout))
            setErrorMessage(undefined)
            if (!IS_PRODUCTION) {
                setVerificationCode(data.verification_code)
            }
        } catch (e) {
            setStatus(CLIENT_STATUS.ERROR)
            setSecondPage(true)
            setErrorMessage(getVerificationError(e))
        } finally {
            setIsLoading(false)
        }
    }

    const changeCode = (code: string) => {
        setCode(code)
    }

    const handleClickCode = async () => {
        if (!fetchByCodeResponse) {
            return
        }

        try {
            await fetchVerifyByCode({
                code,
                token: fetchByCodeResponse.verification_token,
                email,
            })
            void reloadClient()
        } catch (e) {
            setErrorMessage(getVerificationError(e))
        }
    }

    const handleGoBack = () => {
        setSecondPage(false)
        setStatus(CLIENT_STATUS.FORM)
    }

    return (
        <>
            {status !== CLIENT_STATUS.CODE && (
                <>
                    {isSecondPage ? (
                        <SecondHeader onClick={handleGoBack} place={place} />
                    ) : (
                        <Header
                            setIsAuthorization={setIsAuthorization}
                            isAuthorization={isAuthorization}
                            isSecondPage={isSecondPage}
                            place={place}
                        />
                    )}
                </>
            )}
            <div
                className={cn(styles.contentWrapper, {
                    [styles.isMain]: place === CLIENT_AUTHORIZATION_PLACE.MAIN,
                    [styles.isModal]: place === CLIENT_AUTHORIZATION_PLACE.MODAL,
                })}
            >
                {status === CLIENT_STATUS.FORM && (
                    <>
                        {isAuthorization ? (
                            <LoginCard
                                handleSubmit={(...args) => {
                                    validateEmail()
                                    handleSubmit(...args)
                                }}
                                showErrors={showErrors}
                                buttonDisabled={isLoading}
                                emailField={{
                                    value: email,
                                    setValue: (v) => {
                                        if (v) setEmailError(false)
                                        setEmail(v)
                                    },
                                    error: getEmailErrorMessage(
                                        showErrors,
                                        emailError,
                                        emailValidationError
                                    ),
                                    inputClassName: emailInputStyles,
                                }}
                            />
                        ) : (
                            <RegistrationCard
                                handleSubmit={(...args) => {
                                    validateName()
                                    validateEmail()
                                    byPromocode && validatePromocode()
                                    handleSubmit(...args)
                                }}
                                showErrors={showErrors}
                                buttonDisabled={isLoading}
                                nameField={{
                                    value: name,
                                    setValue: (v) => {
                                        if (v) setNameError(false)
                                        setName(v)
                                    },
                                    error: getNameErrorMessage(
                                        showErrors,
                                        nameError,
                                        nameValidationError
                                    ),
                                    inputClassName: nameInputStyles,
                                }}
                                emailField={{
                                    value: email,
                                    setValue: (v) => {
                                        if (v) setEmailError(false)
                                        setEmail(v)
                                    },
                                    error: getEmailErrorMessage(
                                        showErrors,
                                        emailError,
                                        emailValidationError
                                    ),
                                    inputClassName: emailInputStyles,
                                }}
                                promocodeField={
                                    byPromocode
                                        ? {
                                              value: promocode,
                                              setValue: (v) => {
                                                  if (v) setPromocodeError(false)
                                                  setPromocode(v)
                                              },
                                              error: promocodeErrorMessage,
                                              inputClassName: promocodeInputStyles,
                                          }
                                        : undefined
                                }
                            />
                        )}
                    </>
                )}
                {status === CLIENT_STATUS.SUCCESS && (
                    <SuccessLoginPage email={successData.email} place={place} />
                )}
                {status === CLIENT_STATUS.CODE && (
                    <>
                        {verificationCode && (
                            <div
                                className={styles.modalVerificationCode}
                                onClick={() => setCode(verificationCode)}
                            >
                                {verificationCode}
                            </div>
                        )}
                        {isLoading && <Spinner />}
                        {!isLoading && (
                            <>
                                <div className={styles.modalContainer}>
                                    <div className={styles.modalContent}>
                                        <div className={styles.modalHeadline}>
                                            Введите код из&nbsp;письма
                                        </div>
                                        <VerificationCode
                                            code={code}
                                            error={!!errorMessage}
                                            onChangeCode={changeCode}
                                        />
                                        {errorMessage && (
                                            <Text className={styles.error} isSecondary>
                                                {errorMessage}
                                            </Text>
                                        )}
                                    </div>
                                    <Text className={styles.modalVerificationAgain}>
                                        Мы&nbsp;прислали письмо с&nbsp;кодом
                                        <br />
                                        на {email}.<br />
                                        {resendTimeout >= new Date() ? (
                                            <>
                                                Прислать еще раз через&nbsp;
                                                {format(new Date(countdown), "mm:ss")}
                                            </>
                                        ) : (
                                            <button
                                                className={styles.modalVerificationResend}
                                                onClick={() => void authByCode()}
                                            >
                                                Прислать еще раз
                                            </button>
                                        )}
                                    </Text>
                                </div>
                            </>
                        )}
                    </>
                )}
                {status === CLIENT_STATUS.ERROR && (
                    <ErrorPage headline="Ошибка : (" secondary={errorMessage} place={place}>
                        <ActionButtons
                            onClick={() => switchView(!isAuthorization)}
                            showPersonalInfo={place === CLIENT_AUTHORIZATION_PLACE.MODAL}
                            actionButtonLabel={model.errorFallbackLabel}
                        />
                    </ErrorPage>
                )}
            </div>
        </>
    )
}
